<template>
  <div v-if="!isloading">
    <full-page :options="options"
               id="fullpage"
               ref="fullpage">
      <fin-full-page-section :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                             :style="'padding-bottom: ' + finProfilerPaddingBottom + 'px;'">
        <v-row class="pa-3 justify-center">
          <v-col align="center" class="pb-0" cols="12" md="10" xl="7">
            <h1 style="text-align: center">
              <fin-lexicon-text auto-solve text='Welcher Archetyp bist du?'/>
            </h1>
            <p v-if="$vuetify.breakpoint.smAndUp" class="mt-5" style="text-align: center">
              <fin-lexicon-text auto-solve
                                text='Für deine passende Anlagestrategie musst du einen dich charakterisierenden Archetypen bestimmen. Ein Archetyp repräsentiert deine Eigenschaften, Vorlieben und Ziele und stellt sicher, dass die resultierende Anlagestrategie deine individuellen Bedürfnisse erfüllt. Wenn du bereits weißt, worauf es dir ankommt, dann kannst du den Archetypen direkt auswählen.'/>
            </p>
            <p v-if="$vuetify.breakpoint.smAndUp" style="text-align: center">
              <fin-lexicon-text auto-solve text='Du kannst deine Auswahl jederzeit wieder ändern.'/>
            </p>
            <p v-if="$vuetify.breakpoint.xs" class="mt-5" style="text-align: center">
              <fin-lexicon-text auto-solve
                                text='Für deine Anlagestrategie musst du einen Archetyp bestimmen. Er repräsentiert deine Eigenschaften, Vorlieben und Ziele und stellt sicher, dass die resultierende Anlagestrategie deine Bedürfnisse erfüllt. Du kannst 6 einfache Fragen beantworten oder den Archetyp direkt auswählen. Du kannst deine Auswahl jederzeit wieder ändern.'/>
            </p>

            <!-- the user has two alternatives: define his archetype step by step or define it himself -->
            <fin-decision-way-selector v-model="decisionWay"
                                       :mode="previewMode ? 'preview' : 'onboarding'"/>


            <p :style="'font-size: 12px; color:' + $finColors.coolGrey">
              <fin-lexicon-text auto-solve
                                text='Hinweis: Die finCraft-Anwendung berücksichtigt in der aktuellen Version lediglich Aktien und ETFs als Anlagealternativen. Wir weisen ausdrücklich darauf hin, dass diese Produkte nicht für jeden Anleger geeignet sind, insbesondere nicht für solche, die das Risiko eines Totalverlustes nicht tragen können.'/>
            </p>
          </v-col>
        </v-row>
      </fin-full-page-section>

      <template v-if="decisionWay===1">
        <fin-full-page-section :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                               :style="gradientStepbyStep1">
          <fin-step-by-step1 id="step1"
                             :question-index="1"
                             v-model="stepByStepValue['q1']"
                             @next="nextDynamicPage(1)"
                             :animations="animations1"
                             :properties="archetypeClassifications[0]"
                             class="pa-3"/>
        </fin-full-page-section>
        <fin-full-page-section v-if="stepByStepValue['q1']>=0"
                               :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                               :style="gradientStepbyStep2">
          <fin-step-by-step2 id="step2"
                             :question-index="2"
                             v-model="stepByStepValue['q2']"
                             @next="nextDynamicPage(2)"
                             :animations="animations2"
                             :properties="archetypeClassifications[1]"
                             class="pa-3"/>
        </fin-full-page-section>
        <fin-full-page-section v-if="stepByStepValue['q2']>=0"
                               :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                               :style="gradientStepbyStep3">
          <fin-step-by-step3 id="step3"
                             :question-index="3"
                             v-model="stepByStepValue['q3']"
                             @next="nextDynamicPage(3)"
                             :animations="animations3"
                             :properties="archetypeClassifications[2]"
                             class="pa-3"/>
        </fin-full-page-section>
        <fin-full-page-section v-if="stepByStepValue['q3']>=0"
                               :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                               :style="gradientStepbyStep4">
          <fin-step-by-step4 id="step4"
                             :question-index="4"
                             v-model="stepByStepValue['q4']"
                             @next="nextDynamicPage(4)"
                             :animations="animations4"
                             :properties="archetypeClassifications[3]"
                             class="pa-3"/>
        </fin-full-page-section>
        <fin-full-page-section v-if="stepByStepValue['q4']>=0"
                               :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                               :style="gradientStepbyStep5">
          <fin-step-by-step5 id="step5"
                             :question-index="5"
                             v-model="stepByStepValue['q5']"
                             @next="nextDynamicPage(5)"
                             :animations="animations5"
                             :properties="archetypeClassifications[4]"
                             class="pa-3"/>
        </fin-full-page-section>
        <fin-full-page-section v-if="stepByStepValue['q5']>=0"
                               :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                               :style="gradientStepbyStep6">
          <fin-step-by-step6 id="step6"
                             :question-index="6"
                             v-model="stepByStepValue['q6']"
                             @next="nextDynamicPage(6)"
                             :animations="animations6"
                             :properties="archetypeClassifications[5]"
                             class="pa-3"/>
        </fin-full-page-section>
      </template>

      <template v-else-if="decisionWay===2">
        <fin-full-page-section id="section2"
                               ref="section2"
                               class="archetypes-carousel"
                               :style="gradientArchetypeCarousel + 'padding-bottom: ' + finProfilerPaddingBottom + 'px;'"
                               :fullpageBehaviourDisabled="fullpageBehaviourDisabled">
          <v-row>
            <v-col v-on:scroll="() => scrollEventInArchetypCarouselSection()"
                   v-on:wheel="() => scrollEventInArchetypCarouselSection()">
              <h1 class="mb-6" style="text-align: center">
                <fin-lexicon-text auto-solve
                                  text='Wähle deinen Archetyp
'/>
              </h1>
              <fin-archetype-selector ref="selector"
                                      v-model="archeType" class="archetypes"
                                      @input="nextDynamicPage('directChoice')"/>
            </v-col>
          </v-row>
        </fin-full-page-section>
      </template>


      <fin-full-page-section v-if="archeType.archeType"
                             :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                             :style="'background-color: #FFFFFF; padding-bottom: ' + finProfilerPaddingBottom + 'px;'"
                             id="section3"
                             class="archetype-result">

        <fin-archetype-result
          :archetype="archeType.archeType"
          class="pa-3"
          @archetypeOK="showGoToNextStep"
          @reset="reset"
        />

      </fin-full-page-section>

      <!-- padding/margin workaround needed for xs to scroll down properly to the last section -->
      <fin-full-page-section v-if="archeType.archeType"
                             :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                             :style="'padding-bottom: ' + finProfilerPaddingBottom + 'px; margin-bottom: ' + (0-finProfilerPaddingBottom) + 'px'"
                             class="footerSection pt-0" last-section>
        <v-row v-if="!$route.fullPath.includes('finProfiler')"
               :style="'background-color:' + $finColors.primaryDark">
          <v-col>
            <fin-go-to-next-step
              :style="'background-color:' + $finColors.primaryDark + ';max-width: 600px; margin: 0 auto;'"
              buttonBackgroundColor="#ffffff"
              buttonText="Weiter zur Strategie"
              buttonTextColor="#02073a"
              class="pb-4 px-3"
              text="Jetzt wird’s richtig spannend! Hier finden wir gemeinsam heraus, welche Anlagestrategie die richtige für dich ist."
              textColor="#ffffff"
              title="Schritt 2 - Ab zur Strategie"
              titleColor="#ffffff"
              @nextStep="nextStep"/>
          </v-col>
        </v-row>

        <v-row v-else :style="'background-color:' + $finColors.primaryDark"
               class="pa-3">
          <v-col>
            <h2 class="mb-2" style="color: #fff;">Archetyp bestätigen</h2>
            <v-btn @click.native="saveArchetype">Speichern</v-btn>
          </v-col>
        </v-row>
      </fin-full-page-section>
    </full-page>
  </div>
  <v-container v-else fill-height fluid ma-0 style="min-height: 85vh">
    <v-row justify="center" align="center">
      <fin-logo-full dark loading/>
    </v-row>
  </v-container>
</template>

<script>
import FinGoToNextStep from '@/components/pages/onboarding/GoToNextStep/index'
import FinDecisionWaySelector from './DecisionWaySelector/index'
import FinArchetypeSelector from './ArchetypeDirect/ArchetypeSelector/index'
import FinFullPageSection from '@/components/pages/onboarding/FullPageSection'
import FinStepByStep1 from './StepByStep/Step1/index'
import FinStepByStep3 from './StepByStep/Step3/index'
import FinStepByStep2 from './StepByStep/Step2/index'
import FinStepByStep4 from './StepByStep/Step4/index'
import FinStepByStep5 from './StepByStep/Step5/index'
import FinStepByStep6 from './StepByStep/Step6/index'
import FinArchetypeResult from './ArchetypeResult/index'
import {FULL_PAGE_KEY} from '@/js/finConstants'
import FinLexiconText from '@/components/common/lexicon/LexiconText/index'
import {mapActions, mapGetters, mapState} from 'vuex'
import FinLogoFull from '@/components/common/logo/LogoFull/index'
import {handleError} from '@/js/finAwsErrorHandler'
import finBrowser from "@/js/finBrowser"
import AmplitudePush from "@/mixins/AmplitudePush"

export default {
  name: 'finOnboardingStep2',
  mixins: [AmplitudePush],
  components: {
    FinLogoFull,
    FinLexiconText,
    FinArchetypeResult,
    FinStepByStep6,
    FinStepByStep5,
    FinStepByStep4,
    FinStepByStep2,
    FinStepByStep3,
    FinStepByStep1,
    FinGoToNextStep,
    FinFullPageSection,
    FinArchetypeSelector,
    FinDecisionWaySelector
  },
  props: {
    value: {},
    origin: {
      type: String
    }
  },
  data() {
    const value = Object.assign({}, this.$props.value)
    return {
      stepByStepValue: (value.decisionWay === 1 ? value.classification : {}) || {},
      mode: undefined,
      options: {
        licenseKey: FULL_PAGE_KEY,
        controlArrows: true,
        onLeave: this.onLeavePage,
        paddingTop: '0px',
        paddingBottom: '0px',
        responsiveWidth: 0,
        responsiveHeight: 0,
        scrollOverflow: false,
        // scrollOverflowReset: true,// only available with the fullpage-extensions!!
        afterLoad: this.afterLoad,
        afterResize: this.afterResize
      },
      gradientArchetypeCarousel: '',
      stepByStepQuestionBackgroundColor: ['#F0E8DD', '#ABD193', '#EDF1F4', '#EDF1F4', '#EDF1F4', '#EDF1F4'],
      gradientStepbyStep1: '',
      gradientStepbyStep2: '',
      gradientStepbyStep3: '',
      gradientStepbyStep4: '',
      gradientStepbyStep5: '',
      gradientStepbyStep6: '',
      model: value,
      previewMode: this.$route.path.includes('/preview'),
      animations1: [
        {
          svg: {height: '460', width: '460', x: '-80', y: '-50'},
          svgMobiles: {height: '260', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step1/chicken1.json'),
          buttonStyle: {
            position: 'absolute',
            top: '244px',
            left: '211px'
          },
          textStyle: {
            position: 'absolute',
            top: '265px',
            left: '275px',
            fontWeight: 'bold'
          },
          title: 'Kapitalerhalt'
        },
        {
          svg: {height: '460', width: '460', x: '210', y: '-185'},
          svgMobiles: {height: '260', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step1/chicken2.json'),
          buttonStyle: {
            position: 'absolute',
            top: '42px',
            left: '486px'
          },
          textStyle: {
            position: 'absolute',
            top: '0px',
            left: '486px',
            fontWeight: 'bold'
          },
          title: 'Passives Zusatzeinkommen'
        },
        {
          svg: {height: '460', width: '460', x: '505', y: '-50'},
          svgMobiles: {height: '260', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step1/chicken3.json'),
          // position of the dot
          buttonStyle: {
            position: 'absolute',
            top: '240px',
            left: '544px'
          },
          // position of the dot title
          textStyle: {
            position: 'absolute',
            top: '220px',
            left: '385px',
            fontWeight: 'bold'
          },
          // dot title
          title: 'Vermögensaufbau'
        }
      ],
      animations2: [
        {
          svg: {height: '700', width: '700', x: '-225', y: '-85'},
          svgMobiles: {height: '420', width: '250', x: '0', y: '-130'},
          animationData: require('@/assets/onboarding/stepByStep/step2/tree1.json'),
          buttonStyle: {
            position: 'absolute',
            top: '160px',
            left: '140px'
          },
          textStyle: {
            position: 'absolute',
            top: '172px',
            left: '205px',
            fontWeight: 'bold'
          },
          title: 'kurzfristig'
        },
        {
          svg: {height: '700', width: '700', x: '80', y: '-130'},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step2/tree2.json'),
          buttonStyle: {
            position: 'absolute',
            top: '85px',
            left: '460px'
          },
          textStyle: {
            position: 'absolute',
            top: '54px',
            left: '394px',
            fontWeight: 'bold'
          },
          title: 'mittelfristig'
        },
        {
          svg: {height: '700', width: '700', x: '460', y: '-185'},
          svgMobiles: {height: '240', width: '250', x: '0', y: '-55'},
          animationData: require('@/assets/onboarding/stepByStep/step2/tree3.json'),
          buttonStyle: {
            position: 'absolute',
            top: '175px',
            left: '660px'
          },
          textStyle: {
            position: 'absolute',
            top: '188px',
            left: '570px',
            fontWeight: 'bold'
          },
          title: 'langfristig'
        }
      ],
      animations3: [
        {
          svg: {x: "190", y: "-125", width: "520", height: "520"},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-125'},
          animationData: require('@/assets/onboarding/stepByStep/step3/mouse.json'),
          buttonStyle: {
            position: 'absolute',
            top: '210px',
            left: '360px',
            zIndex: '2'
          },
          textStyle: {
            position: 'absolute',
            top: '245px',
            left: '420px',
            fontWeight: 'bold'
          },
          title: 'gering'
        },
        {
          svg: {x: "380", y: "-130", width: "540", height: "540"},
          svgMobiles: {height: '250', width: '250', x: '0', y: '-70'},
          animationData: require('@/assets/onboarding/stepByStep/step3/dog.json'),
          buttonStyle: {
            position: 'absolute',
            top: '200px',
            left: '540px',
            zIndex: '2'
          },
          textStyle: {
            position: 'absolute',
            top: '250px',
            left: '614px',
            fontWeight: 'bold'
          },
          title: 'mittel'
        },
        {
          svg: {x: "610", y: "-110", width: "520", height: "520"},
          svgMobiles: {height: '220', width: '250', x: '0', y: '-45'},
          animationData: require('@/assets/onboarding/stepByStep/step3/tiger.json'),
          buttonStyle: {
            position: 'absolute',
            top: '185px',
            right: '240px',
            zIndex: '2'
          },
          textStyle: {
            position: 'absolute',
            top: '250px',
            left: '833px',
            fontWeight: 'bold'
          },
          title: 'hoch'
        }
      ],
      animations4: [
        {
          svg: {x: '-355', y: '-252', width: '850', height: '850'},
          svgMobiles: {height: '290', width: '250', x: '0', y: '-65'},
          animationData: require('@/assets/onboarding/stepByStep/step4/level1.json'),
          buttonStyle: {
            position: 'absolute',
            top: '225px',
            left: '185px'
          },
          textStyle: {
            position: 'absolute',
            top: '240px',
            left: '250px',
            fontWeight: 'bold'
          },
          title: 'Anfänger'
        },
        {
          svg: {x: '-15', y: '-295', width: '850', height: '850'},
          svgMobiles: {height: '290', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step4/level2.json'),
          buttonStyle: {
            position: 'absolute',
            top: '35px',
            left: '440px'
          },
          textStyle: {
            position: 'absolute',
            top: '50px',
            left: '280px',
            fontWeight: 'bold'
          },
          title: 'Fortgeschrittener'
        },
        {
          svg: {x: '305', y: '-285', width: '850', height: '850'},
          svgMobiles: {height: '290', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step4/level3.json'),
          buttonStyle: {
            position: 'absolute',
            top: '40px',
            left: '760px'
          },
          textStyle: {
            position: 'absolute',
            top: '54px',
            left: '720px',
            fontWeight: 'bold'
          },
          title: 'Profi'
        }
      ],
      animations5: [
        {
          svg: {x: "-280", y: "-300", width: "870", height: "870"},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step5/chair1.json'),
          buttonStyle: {
            position: 'absolute',
            top: '50px',
            left: '215px'
          },
          textStyle: {
            position: 'absolute',
            top: '20px',
            left: '215px',
            fontWeight: 'bold'
          },
          title: 'wenig'
        },
        {
          svg: {x: "40", y: "-315", width: "870", height: "870"},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step5/chair2.json'),
          buttonStyle: {
            position: 'absolute',
            top: '15px',
            left: '480px'
          },
          textStyle: {
            position: 'absolute',
            top: '7px',
            left: '553px',
            fontWeight: 'bold'
          },
          title: 'mittel'
        },
        {
          svg: {x: "320", y: "-315", width: "870", height: "870"},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step5/chair3.json'),
          buttonStyle: {
            position: 'absolute',
            top: '0px',
            left: '720px'
          },
          textStyle: {
            position: 'absolute',
            top: '-5px',
            left: '670px',
            fontWeight: 'bold'
          },
          title: 'hoch'
        }
      ],
      animations6: [
        {
          svg: {x: "-290", y: "-295", width: "870", height: "870"},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step6/fisherman1.json'),
          buttonStyle: {
            position: 'absolute',
            top: '55px',
            left: '255px'
          },
          textStyle: {
            position: 'absolute',
            top: '80px',
            left: '315px',
            fontWeight: 'bold'
          },
          title: 'passiv'
        },
        {
          svg: {x: "0", y: "-290", width: "870", height: "870"},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step6/fisherman2.json'),
          buttonStyle: {
            position: 'absolute',
            top: '60px',
            left: '546px'
          },
          textStyle: {
            position: 'absolute',
            top: '105px',
            left: '590px',
            fontWeight: 'bold'
          },
          title: 'mittel'
        },
        {
          svg: {x: "305", y: "-290", width: "870", height: "870"},
          svgMobiles: {height: '320', width: '250', x: '0', y: '-80'},
          animationData: require('@/assets/onboarding/stepByStep/step6/fisherman3.json'),
          buttonStyle: {
            position: 'absolute',
            top: '48px',
            left: '690px'
          },
          textStyle: {
            position: 'absolute',
            top: '43px',
            left: '750px',
            fontWeight: 'bold'
          },
          title: 'aktiv'
        }
      ],
      fullpageBehaviourDisabled: false
    }
  },
  computed: {
    ...mapState('archetype', ['archetypeClassifications', 'archetypes']),
    ...mapGetters('archetype', ['archetypeFinder', 'loading']),
    decisionWay: {
      get() {
        return this.model?.decisionWay || 0
      },
      set(val) {
        this.$set(this.model, 'decisionWay', val)
      }
    },
    activeStep() {
      if (this.archeType.archeType) {
        return 3
      } else if (this.decisionWay !== undefined) {
        return 2
      } else {
        return 1
      }
    },
    archeType: {
      get() {
        return {
          archeType: this.isloading ? null : this.archetypeFinder?.byArchetypeId(this.model?.archeType),
          individual: this.model?.individual,
          classification: this.model?.classification
        }
      },
      set(val) {
        this.model = Object.assign({}, this.model, {
          archeType: val?.archeType?.id,
          individual: !!val?.individual,
          classification: val?.classification
        })
      }
    },
    isloading() {
      return this.loading
    },
    onboardingPaddingTop() {
      let h = document.querySelector('.fin-onboarding-slider')?.getBoundingClientRect().height
      this.$set(this.options, 'paddingTop', (h ? h : 0) + 'px')
      return h
    },
    finProfilerPaddingTop() {
      let h = document.querySelector('.fin-top-navigation')?.getBoundingClientRect().height
      this.$set(this.options, 'paddingTop', (h ? h : 0) + 'px')
      return h
    },
    finProfilerPaddingBottom() {
      // needed because of footer in finProfiler-Mode
      let h = document.querySelector('.v-footer')?.getBoundingClientRect().height * 2
      if (this.$vuetify.breakpoint.xs) {
        let hMobileNavFooter =
          document.querySelector('.v-bottom-navigation')?.getBoundingClientRect().height * 2
        h = hMobileNavFooter
      }
      this.$set(this.options, 'paddingBottom', (h ? h : 0) + 'px')
      return h
    }
  },
  methods: {
    ...mapActions('archetype', ['loadArchetypeClassifications', 'loadArchetypes', 'loadArchetypesPublic']),
    ...mapActions('auth', ['updateArchetype']),
    showGoToNextStep() {
      this.$refs.fullpage.build()
      this.$refs.fullpage.api.moveSectionDown()
    },
    saveArchetype() {
      this.updateArchetype(this.archeType.archeType.id)
        .then(() => {
          this.$toasted.global.finToast({
            message: 'Dein Archetyp wurde erfolgreich geändert.',
            type: 'success'
          })
          this.$router.push({name: this.origin})
        })
        .catch((e) => {
          console.error('updateArchetype failed', e)
          this.$toasted.global.finToast({
            message: 'Beim Speichern deines Archetyps ist ein Fehler aufgetreten.',
            type: 'error'
          })
        })
    },
    onLeavePage(origin, destination, direction) {
      this.$emit('pageChange', destination.index)
    },
    afterLoad(origin, destination, direction) {
      this.calcGradientStart()
    },
    afterResize(width, height) {
      this.calcGradientStart()
    },
    scrollToSection(section) {
      this.$refs?.fullpage?.api.silentMoveTo(section)
    },
    nextStep() {
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_strategy_starting',
        'step': this.mode + "_strategy_start",
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.$emit('nextStep')
    },
    reset() {
      this.archeType = null
      // otherwise the watch isn't triggered if the user is selecting the same answers, see FINDEV-2921
      this.stepByStepValue = {}
      this.decisionWay = null

      // switch back to step 1
      this.$refs.fullpage?.api.silentMoveTo(1)
    },
    nextDynamicPage(stepAnswered) {
      this.$nextTick(() => {
        if (stepAnswered !== undefined) {
          this.pushToAmplitude({
            'event': 'fincraft_' + this.mode + '_archetype_' + (this.decisionWay === 1 ? 'stepByStep_answer' : 'directChoice'),
            'step': (this.previewMode ? 'preview_' : this.mode + '_') + (stepAnswered === 'directChoice' ? stepAnswered : 'question_' + stepAnswered),
            'step_choice': stepAnswered === 'directChoice' ? this.archeType.archeType.id : this.stepByStepValue['q' + stepAnswered],
            'vue_breakpoint': this.$vuetify.breakpoint.name
          })
        }

        this.$refs.fullpage.build()
        this.$refs.fullpage.api.moveSectionDown()
      })

    },
    setError(e) {
      const error = handleError(e)
      console.error('Onboarding Step2 TODO track and log', error)
    },
    scrollEventInArchetypCarouselSection() {
      this.calcGradientInArchetypCarouselSection()
    },
    calcGradientInArchetypCarouselSection() {
      if (document.querySelector('.archetypeIllu')) {
        const archetypeIllu = document.querySelector('.archetypeIllu')
        const archetypeIlluBottom = archetypeIllu.getBoundingClientRect().bottom
        // 7, because the illustrations have some padding at the bottom
        let backgroundTop = archetypeIlluBottom - 7
        let activeSection = this.$refs.fullpage?.api?.getActiveSection()
        backgroundTop -= activeSection?.item.getBoundingClientRect().top

        this.gradientArchetypeCarousel = 'background: linear-gradient(to bottom, transparent ' + backgroundTop + 'px, ' +
          this.$finColors.primaryDark + ' ' + backgroundTop + 'px);'
      }
    },
    calcGradientInStepByStepQuestion(questionIndex, color) {
      let questionSelectorId = '#step' + questionIndex
      if (document.querySelector(questionSelectorId) && document.querySelector('.step-by-step-illus')) {
        /* 160 damit der Benutzer innerhalb der Sektion hochscrollen kann, ohne dass der Background unten
         weiss bzw. abgebrochen wird. Alternative müsste man das scroll event wie beim Archetyp-Carousel abfangen und diese Methode nochmals aufrufen
         und das activeSession Top abziehen */
        let backgroundTop = document.querySelector(questionSelectorId).getBoundingClientRect().bottom - 160
        backgroundTop -= this.$route.fullPath.includes('finProfiler') ? this.finProfilerPaddingBottom : 0
        backgroundTop -= this.$route.fullPath.includes('finProfiler') ? this.finProfilerPaddingTop : 0

        let res = 'background: linear-gradient(to bottom, transparent ' + backgroundTop + 'px, ' +
          color + ' ' + backgroundTop + 'px);'
        if (questionIndex === 1) {
          this.gradientStepbyStep1 = res
        } else if (questionIndex === 2) {
          this.gradientStepbyStep2 = res
        } else if (questionIndex === 3) {
          this.gradientStepbyStep3 = res
        } else if (questionIndex === 4) {
          this.gradientStepbyStep4 = res
        } else if (questionIndex === 5) {
          this.gradientStepbyStep5 = res
        } else if (questionIndex === 6) {
          this.gradientStepbyStep6 = res
        }
      }
    },
    calcGradientStart() {
      if (this.decisionWay === 1) {
        let index = this.$refs.fullpage?.api?.getActiveSection()?.index
        this.calcGradientInStepByStepQuestion(index, this.stepByStepQuestionBackgroundColor[index - 1])
      } else if (this.decisionWay === 2) {
        this.calcGradientInArchetypCarouselSection()
      }
    }
  },
  mounted() {
    // disable fullpage behaviour in mobiles and tablets
    this.fullpageBehaviourDisabled = finBrowser.IS_MOBILE_OR_TABLET || finBrowser.IS_IOS_OR_ANDROID_APP
    this.$set(this.options, 'responsiveWidth', this.fullpageBehaviourDisabled ? 5000 : 0)
    this.$set(this.options, 'responsiveHeight', this.fullpageBehaviourDisabled ? 5000 : 0)
    this.$set(this.options, 'scrollOverflow', !this.fullpageBehaviourDisabled)

    this.$set(this.options, 'paddingTop', (this.onboardingPaddingTop ? this.onboardingPaddingTop + 'px' : '0px'))

    if (this.$route.fullPath.includes('finProfiler')) {
      this.$set(this.options, 'paddingBottom', this.finProfilerPaddingBottom + 'px')
    }

    this.mode = this.$route.fullPath.includes('finProfiler') ? 'finProfiler' : 'onboarding'

    if (!this.isloading) {
      this.$refs.fullpage?.api.silentMoveTo(this.$refs.fullpage?.$el.querySelectorAll('.section').length)
      this.calcGradientStart()
    } else {
      this.loadArchetypeClassifications()
      if (this.previewMode) {
        this.loadArchetypesPublic()
      } else {
        this.loadArchetypes()
      }
    }

    this.scrollToSection(this.activeStep >= 3 ? 3 : this.activeStep)
  },
  watch: {
    stepByStepValue: {
      handler(val) {
        this.archeType = {
          archeType: this.isloading ? null : this.archetypeFinder?.find(Object.values(val)),
          classification: val,
          individual: true
        }
      },
      deep: true
    },
    decisionWay() {
      if (this.decisionWay) {
        this.archeType = null
        if (this.decisionWay === 1 && this.$vuetify.breakpoint.smAndDown) {
          this.$toasted.global.finToast({
            message: 'Klicke auf eine Kachel, um die Frage zu beantworten.',
            type: 'info'
          })
        }
        this.nextDynamicPage(undefined)
        this.calcGradientStart()
      }
      this.$emit('dirty', true) // to reset next steps' data
    },
    model: {
      handler(val) {
        this.$emit('input', val)
        this.$emit('archetype', this.archeType)
      },
      deep: true
    },
    loading() {
      this.$nextTick(() => {
        this.$refs.fullpage?.api.silentMoveTo(this.$refs.fullpage?.$el.querySelectorAll('.section').length)
      })
    }
  },
  destroyed() {
    //workaround for FINDEV-2514
    document.body.style.overflow = null
  },
}

</script>

<style lang="scss" scoped>
@import "../../../../styles/variables";

.section {
  text-align: center;
}

/* hide svg tooltips */
svg {
  pointer-events: none;
}
</style>
